<template>
    <AppLayout title="Penjelasan Dokumen">
        <Toast />
        <div class="relative">
            <div class="py-4" :class="{ 'pointer-events-none': loading }">
                <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                    <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                        <section class="text-gray-600 body-font">
                            <div class="container px-5 py-5 mx-auto">
                                <div class="mb-5">
                                    <h2 class="text-xl font-semibold mb-4">
                                        Penjelasan Dokumen: {{ dataViewDokumenPengadaan.nama_paket }}
                                        <div class="text-base font-normal text-gray-600 mt-1">
                                            Topik: {{ subject.subject }}
                                        </div>
                                    </h2>
                                    <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                                        <div class="flex items-center">
                                            <i class="pi pi-user mr-2"></i>
                                            <p class="text-sm">
                                                Alias anda dalam penjelasan ini adalah :  <span class="font-semibold">{{ dataViewDokumenPengadaan.current_actor_display_name || 'User' }} </span>
                                            </p>
                                        </div>
                                    </div>
                                    <div class="flex flex-col h-[800px] bg-white rounded-lg shadow">
                                        <div class="flex-1 p-4 overflow-y-auto" ref="chatContainer">
                                            <div v-if="messages.length === 0" class="flex items-center justify-center h-full">
                                                <div class="text-center text-gray-500">
                                                    <i class="pi pi-comments text-4xl mb-2"></i>
                                                    <div class="text-lg">Belum ada pesan</div>
                                                    <div class="text-sm">Mulai diskusi dengan mengirim pesan pertama</div>
                                                </div>
                                            </div>
                                            <div v-else v-for="message in messages" :key="message.id" class="mb-4">
                                                <div 
                                                    class="flex items-start gap-2"
                                                    :class="{
                                                        'justify-end': message.dokumen_pengadaan_actor_id === dataViewDokumenPengadaan.current_actor_id,
                                                    }"
                                                >
                                                    <div 
                                                        class="max-w-[70%] rounded-lg p-3"
                                                        :class="{
                                                            'bg-green-100': message.dokumen_pengadaan_actor_id === dataViewDokumenPengadaan.current_actor_id,
                                                            'bg-blue-50': message.penyedia_id,
                                                            'bg-yellow-50': message.actor?.role_id === 1,
                                                            'bg-purple-50': message.actor?.role_id === 2,
                                                            'bg-gray-100': !message.penyedia_id && !message.actor?.role_id
                                                        }"
                                                    >
                                                        <div class="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                                            {{ message.display_name }}
                                                            <span v-if="message.penyedia_id" 
                                                                class="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600">
                                                                penyedia
                                                            </span>
                                                        </div>
                                                        <div class="text-sm mt-1">{{ message.message }}</div>
                                                        <div class="text-xs text-gray-500 mt-1">
                                                            {{ formatDate(message.created_at) }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="border-t p-4">
                                            <form @submit.prevent="sendMessage" class="flex gap-2">
                                                <InputText
                                                    v-model="newMessage"
                                                    placeholder="Ketik pesan..."
                                                    class="flex-1"
                                                    :disabled="loading"
                                                />
                                                <Button
                                                    type="submit"
                                                    icon="pi pi-send"
                                                    :loading="loading"
                                                />
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            <div v-if="loading" class="loading-overlay">
                <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="3" />
                <div class="loading-text">Memproses...</div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
import { ref, onMounted } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import AppLayout from '@/Layouts/AppLayout.vue';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import dayjs from 'dayjs';
import axios from 'axios';

export default {
    components: {
        AppLayout,
        InputText,
        Button,
        ProgressSpinner,
        Toast
    },
    props: {
        dataViewDokumenPengadaan: {
            type: Object,
            required: true
        },
        subject: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            toast: useToast(),
            newMessage: '',
            loading: false,
            messages: [],
            refreshInterval: null
        }
    },
    methods: {
        formatDate(date) {
            return dayjs(date).format('DD/MM/YYYY HH:mm');
        },
        scrollToBottom() {
            if (this.$refs.chatContainer) {
                this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight;
            }
        },
        loadMessages() {
            axios.get(route('message-penjelasan.index', {
                dokumen_pengadaan_id: this.dataViewDokumenPengadaan.id,
                subject_penjelasan_id: this.subject.id
            })).then(response => {
                this.messages = response.data;
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            }).catch(error => {
                console.error('Error loading messages:', error);
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat pesan ' + error,
                    life: 3000
                });
            });
        },
        sendMessage() {
            if (!this.newMessage.trim() || this.loading) return;
            
            this.loading = true;
            
            axios.post(route('message-penjelasan.store'), {
                dokumen_pengadaan_id: this.dataViewDokumenPengadaan.id,
                subject_penjelasan_id: this.subject.id,
                message: this.newMessage
            }).then(() => {
                this.newMessage = '';
                this.loadMessages();
                this.toast.add({
                    severity: 'success',
                    summary: 'Sukses',
                    detail: 'Pesan terkirim',
                    life: 3000
                });
            }).catch((error) => {
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal mengirim pesan: ' + (error.response?.data?.message || error.message),
                    life: 3000
                });
            }).finally(() => {
                this.loading = false;
            });
        }
    },
    mounted() {
        this.loadMessages();
        // Set up auto-refresh every 10 seconds
        this.refreshInterval = setInterval(() => {
            this.loadMessages();
        }, 10000);
    },
    beforeUnmount() {
        // Clear the interval when component is unmounted
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }
}
</script>

<style scoped>
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.loading-text {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #4B5563;
}
</style>
